import React, { useState } from 'react';
import {
  Avatar,
  Button,
  CssBaseline,
  TextField,
  Link,
  Grid,
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  CardHeader,
  Snackbar,
  Alert,
  AlertTitle,
  CircularProgress,
  Stack,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useStore } from './store/comercioStore';

const theme = createTheme();

function Login() {
  // form state
  const [cuit, setCuit] = useState('');
  const [email, setEmail] = useState('');

  // ui state
  const [msgInfo, setMsgInfo] = useState(false);
  const [msgError, setMsgError] = useState(false);
  const [loading, setLoading] = useState(false);

  // store global
  const { asigna, asignaTipo, asignaNom, asignaSocio } = useStore();

  const navigate = useNavigate();

  // --- helpers ---
  const handleCloseInfo = () => setMsgInfo(false);
  const handleCloseError = () => setMsgError(false);

  const isFormValid = email.trim() !== '' && cuit.trim() !== '';

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isFormValid || loading) return;

    setLoading(true);

    const postData = {
      email: email.toLowerCase(),
      cuit: cuit,
    };

    try {
      const baseURL = process.env.REACT_APP_SERVER_PATH + 'login';

      const res = await fetch(baseURL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-access-token': 'token-value', // TODO: token real
          'Access-Control-Allow-Origin': '*',
        },
        body: JSON.stringify(postData),
      });

      // status codes esperados según tu implementación original
      if (res.status === 200 || res.status === 204 || res.status === 401) {
        const data = await res.json();

        // data[0] tiene toda la info según tu código
        const user = data[0];
        if (!user) {
          // si vino vacío => credenciales inválidas
          setMsgError(true);
          setLoading(false);
          return;
        }

        // persistimos en la store global
        asigna(user.idcomercio);
        asignaTipo(user.tipo);
        asignaSocio(user.socioucip);
        asignaNom(user.razonsocial);

        // routing según tipo
        if (user.tipo === '2') {
          navigate('/menuucip');
        } else if (user.tipo === '3') {
          navigate('/menuCamaras');
        } else {
          // default comercio
          navigate('/menucomercio');
        }
      } else if (res.status === 402 || res.status === 404) {
        // credenciales no válidas
        setMsgError(true);
      } else if (res.status === 409 || res.status === 423) {
        // ejemplo: comercio existe pero no aprobado
        // (este caso no lo tenías, pero lo dejo por si lo querés mapear a msgInfo)
        setMsgInfo(true);
      } else {
        // fallback error desconocido
        setMsgError(true);
      }
    } catch (err) {
      console.error('Login error:', err);
      setMsgError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Container
        component="main"
        maxWidth="xs"
        sx={{
          display: 'flex',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CssBaseline />

        <Card
          variant="outlined"
          sx={{
            width: '100%',
            borderRadius: 3,
            boxShadow: '0 16px 40px rgba(0,0,0,0.08)',
          }}
        >
          <CardHeader
            sx={{
              textAlign: 'center',
              pb: 0,
              '& .MuiCardHeader-title': {
                fontWeight: 600,
                fontSize: '1.15rem',
              },
            }}
            avatar={
              <Avatar
                sx={{
                  bgcolor: 'secondary.main',
                  mx: 'auto',
                  width: 40,
                  height: 40,
                }}
              >
                <LockOutlinedIcon />
              </Avatar>
            }
            title="Acceso a Comercios"
          />

          <CardContent sx={{ pt: 2 }}>
            <Typography
              variant="body2"
              textAlign="center"
              color="text.secondary"
              sx={{ mb: 2 }}
            >
              Ingresá con tu correo y CUIT para continuar.
            </Typography>

            <Box
              component="form"
              onSubmit={handleSubmit}
              noValidate
              sx={{ mt: 1 }}
            >
              {/* EMAIL */}
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Correo Electrónico"
                name="email"
                autoComplete="email"
                placeholder="tu@mail.com"
                value={email}
                onChange={(e) => setEmail(e.target.value.toLowerCase())}
                disabled={loading}
              />

              {/* CUIT */}
              <TextField
                margin="normal"
                required
                fullWidth
                id="cuit"
                label="CUIT"
                name="cuit"
                type="text"
                inputMode="numeric"
                placeholder="20123456789"
                value={cuit}
                onChange={(e) => setCuit(e.target.value)}
                disabled={loading}
                helperText="Sin guiones ni puntos"
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                sx={{ mt: 3, mb: 2 }}
                disabled={!isFormValid || loading}
              >
                {loading ? (
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    spacing={1}
                    sx={{ width: '100%' }}
                  >
                    <CircularProgress size={20} />
                    <span>Verificando…</span>
                  </Stack>
                ) : (
                  'Ingresar'
                )}
              </Button>

              <Grid container spacing={1} sx={{ mb: 2 }}>
                <Grid item xs={12}>
                  <Link
                    href="/registrocomercios"
                    variant="body2"
                    underline="hover"
                    sx={{ display: 'block', textAlign: 'center' }}
                  >
                    ¿No tenés cuenta? Registrate
                  </Link>
                </Grid>
                <Grid item xs={12}>
                  <Link
                    href="/recuperacion"
                    variant="body2"
                    underline="hover"
                    sx={{ display: 'block', textAlign: 'center' }}
                  >
                    Recuperar datos de ingreso
                  </Link>
                </Grid>
              </Grid>

              <Stack
                spacing={1}
                sx={{
                  textAlign: 'center',
                  fontSize: '0.8rem',
                  color: 'text.secondary',
                }}
              >
                <Link
                  href="/Terminos"
                  variant="body2"
                  underline="hover"
                  sx={{ fontSize: '0.8rem' }}
                >
                  Términos y Condiciones
                </Link>

                <Link
                  href="/ComerciosAdheridos"
                  variant="body2"
                  underline="hover"
                  sx={{ fontSize: '0.8rem' }}
                >
                  Comercios Adheridos
                </Link>
              </Stack>
            </Box>
          </CardContent>
        </Card>
      </Container>

      {/* Snackbar: comercio registrado pero pendiente de aprobación */}
      <Snackbar
        open={msgInfo}
        autoHideDuration={6000}
        onClose={handleCloseInfo}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="info"
          variant="filled"
          onClose={handleCloseInfo}
          sx={{ width: '100%' }}
        >
          <AlertTitle>Su comercio está registrado</AlertTitle>
          Debe aguardar aprobación para continuar.
        </Alert>
      </Snackbar>

      {/* Snackbar: credenciales inválidas / error */}
      <Snackbar
        open={msgError}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={handleCloseError}
          sx={{ width: '100%' }}
        >
          <AlertTitle>Error de credenciales</AlertTitle>
          Los datos ingresados no corresponden a un comercio válido.
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}

export default Login;
